import { useCallback, useMemo, useRef } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';

import { useSyncedPref } from '#hooks/useSyncedPref';

import { conMarca, parseMarcas, parsePagosFijos } from './pagadas';

const CLAVE_VISTAS = 'presupuesto:pagadas-vistas';

/**
 * Pagadas ya vistas (y animadas) por mes, en este dispositivo: la animación
 * del sello solo sale la primera vez. Comodidad local (localStorage).
 */
export function leerVistas(mes: string): Set<string> {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_VISTAS) ?? '{}');
    return new Set(Array.isArray(v?.[mes]) ? v[mes] : []);
  } catch {
    return new Set();
  }
}

export function guardarVistas(mes: string, ids: Iterable<string>) {
  try {
    // Solo se guarda el mes en curso: lo viejo se descarta.
    localStorage.setItem(CLAVE_VISTAS, JSON.stringify({ [mes]: [...ids] }));
  } catch {
    // Sin almacenamiento: se anima cada vez, sin más.
  }
}

/**
 * Prefs de «pagadas»: pagos fijos (`pagos-fijos`) y marcas manuales por mes
 * (`pagadas-manuales`). Ver `pagadas.ts`.
 */
export function usePagadas(month: string) {
  const [rawFijos, setRawFijos] = useSyncedPref('pagos-fijos');
  const [rawMarcas, setRawMarcas] = useSyncedPref('pagadas-manuales');
  const fijos = useMemo(() => parsePagosFijos(rawFijos), [rawFijos]);
  const marcas = useMemo(() => parseMarcas(rawMarcas), [rawMarcas]);
  const manuales = useMemo(() => marcas[month] ?? [], [marcas, month]);
  const ultimas = useRef(marcas);
  ultimas.current = marcas;

  const setFijo = useCallback(
    (id: string, fijo: boolean) => {
      const s = new Set(fijos);
      if (fijo) {
        s.add(id);
      } else {
        s.delete(id);
      }
      setRawFijos(JSON.stringify([...s]));
    },
    [fijos, setRawFijos],
  );
  const setFijos = useCallback(
    (ids: readonly string[]) => setRawFijos(JSON.stringify([...new Set(ids)])),
    [setRawFijos],
  );
  const marcar = useCallback(
    (id: string, pagada: boolean | 'no') => {
      const nuevas = conMarca(
        ultimas.current,
        month,
        id,
        pagada,
        monthUtils.currentMonth(),
      );
      ultimas.current = nuevas;
      if (pagada === true) {
        // Marcada a mano: que el sello se vea otra vez.
        const vistas = leerVistas(month);
        vistas.delete(id);
        guardarVistas(month, vistas);
      }
      setRawMarcas(JSON.stringify(nuevas));
    },
    [month, setRawMarcas],
  );

  return { fijos, manuales, setFijo, setFijos, marcar };
}
